import { FC } from 'react'
import { useForm } from 'local-react-hook-form'
import { useTranslation } from 'react-i18next'
import { ClientError } from 'graphql-request'
import { useRouter } from 'next/router'
import {
  validateRequired,
  useFormSubmitState,
  validateEmail,
  SubmitButton,
  FormTextField,
  FormResponseMessage,
  ActionItemsContainer,
} from '@sans-souci/forms'

import { createCustomerClient } from '../customerService'
import { useCustomerContext } from '../customerContext'

import { formatResponseErrors } from '../helpers/formatResponseErrors'
import { LoginRegisterFormWrapper } from '../components/LoginRegisterFormWrapper'
import { Form } from '../components/Form'
import { ShopifyCustomerUserErrorsFieldsFragment } from '@sans-souci/shopify-sdk'
import { Link } from '@sans-souci/components'
import { ROUTE_ACCOUNT_LOGIN } from '@sans-souci/configs'

interface RegisterFormProps {
  className?: string
  shouldLoginOnSuccess?: true
  onSubmitSuccess?: () => void
}

type FormValues = {
  acceptsMarketing?: boolean
  email: string
  firstName?: string
  lastName?: string
  password: string
}

const defaultValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  acceptsMarketing: false,
}

export const RegisterForm: FC<RegisterFormProps> = ({
  className,
  shouldLoginOnSuccess = true,
  onSubmitSuccess,
}) => {
  const { t } = useTranslation('forms')
  const form = useForm<FormValues>({
    defaultValues,
  })

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    clearErrors,
  } = form

  const { locale } = useRouter()

  const customerClient = createCustomerClient({ locale })
  const { setToken } = useCustomerContext()

  const {
    invalid,
    success,
    setSuccess,
    setError,
    setFieldError,
    submitMessage,
  } = useFormSubmitState<FormValues>({ form, defaultValues })

  const handleResponseErrors = (
    errors: ShopifyCustomerUserErrorsFieldsFragment[],
  ) => {
    const { fieldErrors, generalError } =
      formatResponseErrors<FormValues>(errors)
    fieldErrors.forEach((e) => setFieldError(e.fieldName, e.message))
    return setError(generalError)
  }

  const onSubmit = async (data: FormValues) => {
    try {
      clearErrors()
      const response = await customerClient.create({ input: data })
      const errors = response?.customerCreate?.customerUserErrors

      if (errors?.length) {
        return handleResponseErrors(errors)
      }

      const customer = response?.customerCreate?.customer
      if (!customer) {
        return setError()
      }

      if (shouldLoginOnSuccess) {
        const { email, password } = data
        const loginResponse = await customerClient.createToken({
          email,
          password,
        })

        const loginErrors =
          loginResponse?.customerAccessTokenCreate?.customerUserErrors || []

        if (loginErrors.length) {
          return handleResponseErrors(loginErrors)
        }

        const token =
          loginResponse?.customerAccessTokenCreate?.customerAccessToken
            ?.accessToken
        if (!token) {
          return setError()
        }

        setToken(token)
      }

      setSuccess()
      return onSubmitSuccess && onSubmitSuccess()
    } catch (e: unknown) {
      if (e instanceof ClientError) {
        return setError(e.response.errors?.[0].message)
      }
      return setError()
    }
  }

  return (
    <LoginRegisterFormWrapper>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className={className}
        name="register-form"
      >
        <FormTextField
          control={control}
          rules={validateRequired(t)}
          autoComplete="name"
          name="firstName"
          label={t('registerForm.firstNameFieldLabel') + '*'}
        />
        <FormTextField
          control={control}
          rules={validateRequired(t)}
          autoComplete="family-name"
          name="lastName"
          label={t('registerForm.lastNameFieldLabel') + '*'}
        />
        <FormTextField
          control={control}
          rules={{ ...validateRequired(t), ...validateEmail(t) }}
          autoComplete="email"
          type="email"
          name="email"
          id="email-register"
          label={t('registerForm.emailFieldLabel') + '*'}
        />
        <FormTextField
          control={control}
          rules={validateRequired(t)}
          autoComplete="new-password"
          name="password"
          type="password"
          id="password-register"
          label={t('registerForm.passwordFieldLabel') + '*'}
        />

        <ActionItemsContainer>
          <SubmitButton
            text={t('registerForm.submitButtonLabel')}
            helpText={submitMessage}
            invalid={invalid}
            success={success}
            loading={isSubmitting}
          />
          <Link appearance={'ButtonTransparent'} href={ROUTE_ACCOUNT_LOGIN}>
            {t('registerForm.loginButtonLabel')}
          </Link>
        </ActionItemsContainer>
        {submitMessage && (
          <FormResponseMessage
            invalid={invalid}
            success={success}
            message={submitMessage}
          />
        )}
      </Form>
    </LoginRegisterFormWrapper>
  )
}
